import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Eye, EyeSlash, ShieldCheck, Warning, CheckCircle,
} from "@phosphor-icons/react";
import { api, formatApiError, clearToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const PWD_RULES = [
    { test: (v) => v.length >= 8, label: "At least 8 characters" },
    { test: (v) => /[A-Za-z]/.test(v), label: "Contains at least one letter" },
    { test: (v) => /\d/.test(v), label: "Contains at least one digit" },
];

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ kind: "verifying" }); // verifying | invalid | ready | done
    const [tokenMeta, setTokenMeta] = useState(null);
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/auth/reset-password/${encodeURIComponent(token)}/verify`);
                if (cancelled) return;
                setTokenMeta(data);
                setState({ kind: "ready" });
            } catch (err) {
                if (cancelled) return;
                setState({
                    kind: "invalid",
                    message: formatApiError(err, "Invalid or expired reset link."),
                });
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const ruleResults = PWD_RULES.map((r) => ({ ...r, ok: r.test(pwd) }));
    const allRulesOk = ruleResults.every((r) => r.ok);
    const passwordsMatch = pwd.length > 0 && pwd === pwd2;
    const canSubmit = allRulesOk && passwordsMatch && !submitting;

    const submit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            // Iter 54 — Successful reset invalidates EVERY existing session for
            // this user (backend enforces via password_changed_at).  Clear any
            // local token we might be holding (defensive) and bounce the user
            // to login.
            clearToken();
            setState({ kind: "done" });
        } catch (err) {
            toast.error(formatApiError(err, "Could not reset your password"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="reset-password-page">
            {/* Left: form */}
            <div className="flex items-center justify-center px-6 sm:px-12 py-10">
                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-2.5 mb-12">
                        <div className="h-8 w-8 bg-slate-900 rounded-md flex items-center justify-center">
                            <span className="text-white font-display font-bold text-sm">R</span>
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight text-slate-900">REEDOS</div>
                            <div className="reedos-eyebrow text-[9px]">Billing &amp; Commercial</div>
                        </div>
                    </div>

                    {state.kind === "verifying" && (
                        <div data-testid="reset-verifying">
                            <div className="reedos-eyebrow mb-3">Validating link…</div>
                            <h1 className="font-display text-2xl font-bold text-slate-900">Checking your reset link</h1>
                            <p className="text-sm text-slate-500 mt-3">One moment.</p>
                        </div>
                    )}

                    {state.kind === "invalid" && (
                        <div data-testid="reset-invalid">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-rose-50 border border-rose-200 rounded-full mb-5">
                                <Warning size={22} weight="fill" className="text-rose-700" />
                            </div>
                            <div className="reedos-eyebrow mb-3 text-rose-700">Link expired or invalid</div>
                            <h1 className="font-display text-3xl font-bold text-slate-900 leading-[1.1]">
                                We couldn't verify your reset link.
                            </h1>
                            <p className="text-sm text-slate-500 mt-3">
                                {state.message} The link may have expired (1-hour limit), been used already, or be malformed.
                            </p>
                            <div className="space-y-2 mt-6">
                                <Link to="/forgot-password" data-testid="reset-request-new-link">
                                    <Button className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                                        Request a new reset link
                                        <ArrowRight size={16} weight="bold" />
                                    </Button>
                                </Link>
                                <Link to="/login">
                                    <Button variant="outline" className="w-full h-10">Back to sign in</Button>
                                </Link>
                            </div>
                        </div>
                    )}

                    {state.kind === "ready" && (
                        <>
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 mb-4"
                                data-testid="reset-back-to-login"
                            >
                                <ArrowLeft size={12} weight="bold" /> Back to sign in
                            </Link>
                            <div className="reedos-eyebrow mb-3">Choose a new password</div>
                            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 leading-[1.05]">
                                Set a new password
                            </h1>
                            <p className="text-sm text-slate-500 mt-3">
                                Resetting the password for <span className="font-mono text-slate-900" data-testid="reset-email-display">{tokenMeta?.email}</span>. After saving, every active session for this account will be signed out — you'll need to sign in again on every device.
                            </p>

                            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                                <div>
                                    <Label htmlFor="new-pwd" className="text-xs font-semibold text-slate-700">New password</Label>
                                    <div className="relative mt-1.5">
                                        <Input
                                            id="new-pwd"
                                            type={showPwd ? "text" : "password"}
                                            autoComplete="new-password"
                                            required
                                            value={pwd}
                                            onChange={(e) => setPwd(e.target.value)}
                                            data-testid="reset-pwd-input"
                                            className="h-10 bg-white pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPwd((s) => !s)}
                                            className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-700"
                                            aria-label="Toggle password visibility"
                                            data-testid="reset-toggle-pwd"
                                        >
                                            {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="confirm-pwd" className="text-xs font-semibold text-slate-700">Confirm new password</Label>
                                    <Input
                                        id="confirm-pwd"
                                        type={showPwd ? "text" : "password"}
                                        autoComplete="new-password"
                                        required
                                        value={pwd2}
                                        onChange={(e) => setPwd2(e.target.value)}
                                        data-testid="reset-pwd-confirm-input"
                                        className="h-10 bg-white mt-1.5"
                                    />
                                    {pwd2.length > 0 && !passwordsMatch && (
                                        <p className="text-[11px] text-rose-700 mt-1" data-testid="reset-pwd-mismatch">Passwords don't match.</p>
                                    )}
                                </div>

                                <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="reset-rules">
                                    <div className="reedos-eyebrow text-[9px] mb-2">Password requirements</div>
                                    <ul className="space-y-1">
                                        {ruleResults.map((r) => (
                                            <li key={r.label} className={`flex items-center gap-1.5 text-[11px] ${r.ok ? "text-emerald-700" : "text-slate-500"}`}>
                                                <CheckCircle size={11} weight={r.ok ? "fill" : "regular"} />
                                                {r.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={!canSubmit}
                                    data-testid="reset-submit-btn"
                                    className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium gap-1.5"
                                >
                                    {submitting ? "Resetting…" : "Reset password & sign out everywhere"}
                                    {!submitting && <ArrowRight size={16} weight="bold" />}
                                </Button>
                            </form>
                        </>
                    )}

                    {state.kind === "done" && (
                        <div data-testid="reset-done">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full mb-5">
                                <ShieldCheck size={22} weight="fill" className="text-emerald-700" />
                            </div>
                            <div className="reedos-eyebrow mb-3">All set</div>
                            <h1 className="font-display text-3xl font-bold text-slate-900 leading-[1.1]">
                                Password reset successfully.
                            </h1>
                            <p className="text-sm text-slate-500 mt-3">
                                Every existing session for this account has been signed out for security. Please sign in again with your new password.
                            </p>
                            <Button
                                onClick={() => navigate("/login")}
                                data-testid="reset-go-to-login-btn"
                                className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white gap-1.5 mt-6"
                            >
                                Continue to sign in
                                <ArrowRight size={16} weight="bold" />
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Right: branded panel */}
            <div className="hidden lg:flex bg-slate-900 text-white p-12 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.06]" style={{
                    backgroundImage:
                        "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                }} />
                <div className="relative z-10 flex flex-col justify-between w-full">
                    <div>
                        <div className="reedos-eyebrow text-slate-400">Secure recovery</div>
                        <h2 className="font-display text-4xl tracking-tight font-bold mt-2 leading-[1.1]">
                            One-time, time-bound,<br />zero stale access.
                        </h2>
                        <p className="text-slate-300 text-sm mt-4 max-w-md">
                            Reset links are single-use and expire in 60 minutes. Every successful reset invalidates every existing session — so a compromised device, a forgotten browser tab, or a shared workstation is never a back door into your billing data.
                        </p>
                    </div>
                    <div className="text-[11px] text-slate-500 tracking-wide mt-10">
                        REEDOS · Billing &amp; Commercial Management System (BCMS)
                    </div>
                </div>
            </div>
        </div>
    );
}
